import React, { useState, useEffect } from 'react';
import './AssessmentForm.css';
import IncompleteTabWarning from './IncompleteTabWarning';

const RCADS47Component = ({ reportId, api, onSubmit, onSave, isPublished, sectionName }) => {
  const isParent = sectionName === 'rcads47parent';
  const sectionPath = isParent ? 'rcads47parent' : 'rcads47';
  const [responses, setResponses] = useState({});
  const [savingStates, setSavingStates] = useState({}); // { questionIndex: 'saving' | 'saved' | 'error' }
  const [saveTimeouts, setSaveTimeouts] = useState({}); // Track debounce timeouts
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastCompletedAssessment, setLastCompletedAssessment] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [tScores, setTScores] = useState({});

  const scaleLabels = [
    { value: 0, label: 'Never' },
    { value: 1, label: 'Sometimes' },
    { value: 2, label: 'Often' },
    { value: 3, label: 'Always' }
  ];

  const fieldNames = [
    "q_1_gad_worry_about_things",
    "q_2_mdd_feel_sad_empty",
    "q_3_pd_problem_heart_beats_fast",
    "q_4_sp_feel_scared_talk_class",
    "q_5_sad_worry_away_from_parents",
    "q_6_mdd_nothing_much_fun",
    "q_7_sp_afraid_looking_foolish",
    "q_8_sp_worry_what_others_think",
    "q_9_sad_feel_scared_sleep_own",
    "q_10_ocd_think_special_thoughts",
    "q_11_mdd_no_energy_things",
    "q_12_gad_worry_badly_schoolwork",
    "q_13_gad_worry_awful_happen_family",
    "q_14_pd_suddenly_cant_breathe",
    "q_15_mdd_feel_worthless",
    "q_16_mdd_no_good_anything",
    "q_17_ocd_bad_things_happen",
    "q_18_sad_worry_something_happen_parents",
    "q_19_sad_feel_scared_away_home",
    "q_20_pd_suddenly_dizzy_faint",
    "q_21_sp_feel_scared_take_test",
    "q_22_ocd_think_bad_thoughts",
    "q_23_sad_worry_parents_hurt",
    "q_24_mdd_feel_like_dont_care",
    "q_25_pd_suddenly_tremble_shake",
    "q_26_sp_afraid_make_mistakes",
    "q_27_mdd_feel_lonely",
    "q_28_gad_worry_what_think",
    "q_29_sp_worry_do_something_stupid",
    "q_30_sp_scared_look_stupid",
    "q_31_pd_worry_look_foolish",
    "q_32_sad_feel_scared_alone_house",
    "q_33_ocd_feel_something_terrible",
    "q_34_sad_trouble_going_places_parents",
    "q_35_gad_worry_what_happening",
    "q_36_pd_suddenly_feel_really_scared",
    "q_37_gad_unable_stop_worrying",
    "q_38_pd_worry_going_crazy",
    "q_39_ocd_check_things_over",
    "q_40_mdd_trouble_sleeping",
    "q_41_sp_worry_people_laugh",
    "q_42_pd_suddenly_feel_confused",
    "q_43_ocd_wash_hands_over",
    "q_44_sp_feel_scared_others_watching",
    "q_45_pd_cant_think_clearly",
    "q_46_mdd_feel_restless",
    "q_47_mdd_feel_tired"
  ];

  const questions = [
    "I worry about things",
    "I feel sad or empty",
    "When I have a problem, I get a funny feeling in my stomach",
    "I worry when I think I have done poorly at something",
    "I would feel afraid of being on my own at home",
    "Nothing is much fun anymore",
    "I feel scared when I have to take a test",
    "I feel worried when I think someone is angry with me",
    "I worry about being away from my parent",
    "I am bothered by bad or silly thoughts or pictures in my mind",
    "I have trouble sleeping",
    "I worry that I will do badly at my school work",
    "I worry that something awful will happen to someone in my family",
    "I suddenly feel as if I can't breathe when there is no reason for this",
    "I have problems with my appetite",
    "I have to keep checking that I have done things right (like the switch is off, or the door is locked)",
    "I feel scared if I have to sleep on my own",
    "I have trouble going to school in the mornings because I feel nervous or afraid",
    "I have no energy for things",
    "I worry I might look foolish",
    "I am tired a lot",
    "I worry that bad things will happen to me",
    "I can't seem to get bad or silly thoughts out of my head",
    "When I have a problem, my heart beats really fast",
    "I cannot think clearly",
    "I suddenly start to tremble or shake when there is no reason for this",
    "I worry that something bad will happen to me",
    "When I have a problem, I feel shaky",
    "I feel worthless",
    "I worry about making mistakes",
    "I have to think of special thoughts (like numbers or words) to stop bad things from happening",
    "I worry what other people think of me",
    "I am afraid of being in crowded places (like shopping centres, the movies, buses, busy playgrounds)",
    "All of a sudden I feel really scared for no reason at all",
    "I worry about what is going to happen",
    "I suddenly become dizzy or faint when there is no reason for this",
    "I think about death",
    "I feel afraid if I have to talk in front of my class",
    "My heart suddenly starts to beat too quickly for no reason",
    "I feel like I don't want to move",
    "I worry that I will suddenly get a scared feeling when there is nothing to be afraid of",
    "I have to do some things over and over again (like washing my hands, cleaning or putting things in a certain order)",
    "I feel afraid that I will make a fool of myself in front of people",
    "I have to do some things in just the right way to stop bad things from happening",
    "I worry when I go to bed at night",
    "I would feel scared if I had to stay away from home overnight",
    "I feel restless"
  ];

  const parentQuestions = [
    "My child worries about things",
    "My child feels sad or empty",
    "When my child has a problem, he/she gets a funny feeling in his/her stomach",
    "My child worries when he/she thinks he/she has done poorly at something",
    "My child feels afraid of being alone at home",
    "Nothing is much fun for my child anymore",
    "My child feels scared when taking a test",
    "My child worries when he/she thinks someone is angry with him/her",
    "My child worries about being away from me",
    "My child is bothered by bad or silly thoughts or pictures in his/her mind",
    "My child has trouble sleeping",
    "My child worries about doing badly at school work",
    "My child worries that something awful will happen to someone in the family",
    "My child suddenly feels as if he/she can't breathe when there is no reason for this",
    "My child has problems with his/her appetite",
    "My child has to keep checking that he/she has done things right (like the switch is off, or the door is locked)",
    "My child feels scared to sleep on his/her own",
    "My child has trouble going to school in the mornings because of feeling nervous or afraid",
    "My child has no energy for things",
    "My child worries about looking foolish",
    "My child is tired a lot",
    "My child worries that bad things will happen to him/her",
    "My child can't seem to get bad or silly thoughts out of his/her head",
    "When my child has a problem, his/her heart beats really fast",
    "My child cannot think clearly",
    "My child suddenly starts to tremble or shake when there is no reason for this",
    "My child worries that something bad will happen to him/her",
    "When my child has a problem, he/she feels shaky",
    "My child feels worthless",
    "My child worries about making mistakes",
    "My child has to think of special thoughts (like numbers or words) to stop bad things from happening",
    "My child worries what other people think of him/her",
    "My child is afraid of being in crowded places (like shopping centers, the movies, buses, busy playgrounds)",
    "All of a sudden my child will feel really scared for no reason at all",
    "My child worries about what is going to happen",
    "My child suddenly becomes dizzy or faint when there is no reason for this",
    "My child thinks about death",
    "My child feels afraid if he/she have to talk in front of the class",
    "My child's heart suddenly starts to beat too quickly for no reason",
    "My child feels like he/she doesn't want to move",
    "My child worries that he/she will suddenly get a scared feeling when there is nothing to be afraid of",
    "My child has to do some things over and over again (like washing hands, cleaning, or putting things in a certain order)",
    "My child feels afraid that he/she will make a fool of him/herself in front of people",
    "My child has to do some things in just the right way to stop bad things from happening",
    "My child worries when in bed at night",
    "My child would feel scared if he/she had to stay away from home overnight",
    "My child feels restless"
  ];

  const activeQuestions = isParent ? parentQuestions : questions;

  // Score display definitions - maps to backend property names in data.subscales
  const scoreDisplayConfig = [
    { label: 'Generalized Anxiety (GAD)', rawKey: 'gadScore', tKey: 'gadtScore' },
    { label: 'Social Phobia', rawKey: 'socialPhobiaScore', tKey: 'socialPhobiaTScore' },
    { label: 'Separation Anxiety', rawKey: 'separationAnxietyScore', tKey: 'separationAnxietyTScore' },
    { label: 'Panic Disorder', rawKey: 'panicDisorderScore', tKey: 'panicDisorderTScore' },
    { label: 'OCD', rawKey: 'ocdScore', tKey: 'ocdtScore' },
    { label: 'Depression (MDD)', rawKey: 'depressionScore', tKey: 'depressionTScore' },
    { label: 'Total Anxiety', rawKey: 'totalAnxietyScore', tKey: 'totalAnxietyTScore', isTotal: true },
    { label: 'Total Internalizing', rawKey: 'totalInternalizingScore', tKey: 'totalInternalizingTScore', isTotal: true },
  ];

  // T-score threshold classification
  const getTScoreClass = (tScore) => {
    if (tScore == null) return '';
    if (tScore >= 70) return 'tscore-clinical';
    if (tScore >= 65) return 'tscore-borderline';
    return 'tscore-normal';
  };

  const getTScoreLabel = (tScore) => {
    if (tScore == null) return '';
    if (tScore >= 70) return 'Clinical';
    if (tScore >= 65) return 'Borderline';
    return '';
  };

  // Calculate completion status
  const answeredCount = Object.keys(responses).length;
  const totalQuestions = activeQuestions.length;
  const isComplete = answeredCount === totalQuestions;
  const isNotStarted = answeredCount === 0;
  const isIncomplete = answeredCount > 0 && answeredCount < totalQuestions;

  // Status for tab indicator (exported via getStatus function)
  const getStatus = () => {
    // Assessment is fully optional — if not showing questions, count as complete (skipped)
    if (!showQuestions && isNotStarted) {
      return 'complete';
    }
    if (isComplete) {
      return 'complete';
    }
    if (isNotStarted) {
      return 'not-started';
    }
    return 'incomplete';
  };

  // Load existing responses from API
  useEffect(() => {
    if (!reportId) {
      setLoadError('No report ID was provided to the assessment.');
      setLoading(false);
      return;
    }

    const loadExistingResponses = async () => {
      try {
        setLoading(true);
        const apiResponse = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionPath}`);

        if (apiResponse.ok) {
          const data = await apiResponse.json();

          // Capture previous assessment data
          setLastCompletedAssessment(data.lastCompletedAssessmentForCase || null);

          // Assessment is fully optional — only show questions if already started
          const shouldShowQuestions = data.hasStartedForThisReport;
          setShowQuestions(shouldShowQuestions);

          // Capture T-scores from API
          if (data.subscales) {
            setTScores(data.subscales);
          }

          // Convert field names back to question indexes
          const indexedResponses = {};
          fieldNames.forEach((fieldName, index) => {
            if (data.questions && data.questions[fieldName] != null) {
              indexedResponses[index] = data.questions[fieldName];
            }
          });

          setResponses(indexedResponses);

          // Mark all loaded responses as saved
          const savedStates = {};
          Object.keys(indexedResponses).forEach(index => {
            savedStates[index] = 'saved';
          });
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading existing responses:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingResponses();
  }, [reportId, sectionPath]);

  // API call stub for saving individual response
  const saveResponse = async (questionIndex, value) => {
    if (isPublished) {
      return;
    }
    if (!reportId) {
      console.error('Cannot save RCADS-47 response: no reportId provided');
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'error'
      }));
      return;
    }
    console.log(`Saving RCADS-47 response: Question ${questionIndex + 1}, Value: ${value}`);

    const fieldName = fieldNames[questionIndex];

    try {
      // Actual API call:
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionPath}/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          assessmentType: 'RCADS-47',
          questionIndex,
          questionNumber: questionIndex + 1,
          value: parseInt(value),
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Save failed with HTTP ${response.status}`);
      }

      // Refresh T-scores from API after save
      const refreshResponse = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionPath}`);
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        if (refreshData.subscales) {
          setTScores(refreshData.subscales);
        }
      }

      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'saved'
      }));

      if (onSave) {
        onSave();
      }

      // Keep saved indicator persistent - don't clear it

    } catch (error) {
      console.error(`Error saving response for question ${questionIndex + 1}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'error'
      }));
    }
  };

  const handleResponseChange = (questionIndex, value) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: parseInt(value)
    }));

    // Clear any existing timeout for this question
    if (saveTimeouts[questionIndex]) {
      clearTimeout(saveTimeouts[questionIndex]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [questionIndex]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveResponse(questionIndex, value);
    }, 500); // Wait 500ms after user stops changing responses

    setSaveTimeouts(prev => ({
      ...prev,
      [questionIndex]: timeoutId
    }));
  };

  const handleReassess = () => {
    setShowQuestions(true);
  };

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading existing responses...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading responses: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  // Show skip/optional screen if user hasn't started the assessment
  if (!showQuestions) {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
      <div className="assessment-container">
        <div className="previous-assessment-info">
          {lastCompletedAssessment ? (
            <>
              <h2>Re-Assess?</h2>
              <p className="previous-assessment-notice">
                A previous assessment was made on {lastCompletedAssessment.reportType} report dated {formatDate(lastCompletedAssessment.reportDate)}.
              </p>

              <div className="reassess-action">
                <button
                  className="reassess-button"
                  onClick={handleReassess}
                >
                  Re-Assess
                </button>
              </div>

              <h2 className="scores-heading">Scores from {lastCompletedAssessment.reportType} on {formatDate(lastCompletedAssessment.reportDate)}</h2>

              <div className="scores-grid">
                {scoreDisplayConfig.map(({ label, rawKey, tKey, isTotal }) => (
                  <div key={rawKey} className={`score-item ${isTotal ? 'total' : ''}`}>
                    <span className="score-label">{label}:</span>
                    <span className="score-value">{lastCompletedAssessment.scores[rawKey]}</span>
                    {lastCompletedAssessment.scores[tKey] != null && (
                      <span className={`tscore-value ${getTScoreClass(lastCompletedAssessment.scores[tKey])}`}>
                        T: {lastCompletedAssessment.scores[tKey]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2>{isParent ? 'RCADS-47 Parent/Caregiver Assessment' : 'RCADS-47 Assessment'}</h2>
              <p className="previous-assessment-notice">
                This assessment is optional. You may skip it or start the assessment below.
              </p>

              <div className="reassess-action">
                <button
                  className="reassess-button"
                  onClick={handleReassess}
                >
                  Start Assessment
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>{isParent ? 'RCADS-47 Parent/Caregiver Assessment' : 'RCADS-47 Assessment'}</h2>
        <p>{isParent
          ? 'Please indicate how often each statement applies to your child by selecting the appropriate option.'
          : 'Please indicate how often each statement applies to you by selecting the appropriate option.'
        }</p>
      </div>

      <div className="scale-header sticky-header">
        <div className="scale-labels">
          <div className="scale-label">
            {/* Empty space above question column */}
          </div>
          {scaleLabels.map(scale => (
            <div key={scale.value} className="scale-label" aria-label={`Scale option: ${scale.label}`}>
              <span className="scale-value">{scale.value}</span>
              <span className="scale-text">{scale.label}</span>
            </div>
          ))}
          <div className="scale-label save-status-header">
            <span className="scale-text">Status</span>
          </div>
        </div>
      </div>

      <div className="assessment-form">
        {isIncomplete && (
          <IncompleteTabWarning
            message={`You have answered ${answeredCount} of ${totalQuestions} questions. Please complete all questions.`}
          />
        )}

        <div className="questions-container">
          {activeQuestions.map((question, index) => (
            <fieldset key={index} className="question-fieldset">
              <legend className="question-legend">
                {index + 1}. {question}
              </legend>
              <div className="response-options">
                {scaleLabels.map(scale => (
                  <div key={scale.value} className="radio-container">
                    <input
                      type="radio"
                      id={`q${index}_${scale.value}`}
                      name={`question_${index}`}
                      value={scale.value}
                      checked={responses[index] === scale.value}
                      onChange={(e) => handleResponseChange(index, e.target.value)}
                      className="response-radio"
                    />
                    <label
                      htmlFor={`q${index}_${scale.value}`}
                      className="radio-label sr-only"
                    >
                      {scale.label}
                    </label>
                  </div>
                ))}
              </div>
              <div className="save-status-container">
                {savingStates[index] === 'saving' && (
                  <div className="save-status saving" aria-label="Saving response">
                    <div className="spinner"></div>
                  </div>
                )}
                {savingStates[index] === 'saved' && (
                  <div className="save-status saved" aria-label="Response saved">
                    <span className="checkmark">✓</span>
                  </div>
                )}
                {savingStates[index] === 'error' && (
                  <div className="save-status error" aria-label="Save failed">
                    <span className="error-mark">⚠</span>
                  </div>
                )}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="total-scores">
          <h3>Scores</h3>
          <div className="scores-grid">
            {scoreDisplayConfig.map(({ label, rawKey, tKey, isTotal }) => (
              <div key={rawKey} className={`score-item ${isTotal ? 'total' : ''}`}>
                <span className="score-label">{label}:</span>
                <span className="score-value">Raw Score: {tScores[rawKey] != null ? tScores[rawKey] : ''}</span>
                <span className={`tscore-value ${getTScoreClass(tScores[tKey])}`}>
                  T-Score: {tScores[tKey] != null ? tScores[tKey] : ''}
                  {tScores[tKey] != null && getTScoreLabel(tScores[tKey]) && (
                    <span className="tscore-threshold"> ({getTScoreLabel(tScores[tKey])})</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      </fieldset>

    </div>
  );
};

// Helper function to get RCADS-47 status from API data
export const getRCADS47Status = (apiData) => {
  if (!apiData) {
    return 'not-started';
  }

  // Assessment is fully optional — if not started, treat as complete (skipped)
  if (!apiData.hasStartedForThisReport) {
    return 'complete';
  }

  // Count answered questions
  const answeredCount = apiData.questions ? Object.keys(apiData.questions).filter(
    key => apiData.questions[key] !== undefined && apiData.questions[key] !== null
  ).length : 0;

  const totalQuestions = 47;

  if (answeredCount === totalQuestions) {
    return 'complete';
  }
  if (answeredCount === 0) {
    return 'not-started';
  }
  return 'incomplete';
};

export default RCADS47Component;